import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Loader2, Activity as ActivityIcon } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { listActivityFeed } from "@/lib/activity.functions";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/activity")({
  component: ActivityPage,
  head: () => ({
    meta: [
      { title: "Activity — MissionReady" },
      { name: "description", content: "Real-time feed of guard activity, reports, scans, and alerts." },
    ],
  }),
});

const SOURCE_LABEL: Record<string, string> = {
  time_clock: "Time Punch",
  dispatch: "Dispatch",
  geofence: "Geofence",
  patrol: "Guard Tour",
  scan: "Guard Tour Scan",
  incident: "Report",
  sos: "SOS",
  message: "Message",
};

function ActivityPage() {
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const fetchFeed = useServerFn(listActivityFeed);
  const [tick, setTick] = useState(0);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["activity-feed", orgId, tick],
    queryFn: () => fetchFeed({ data: { orgId: orgId!, limit: 100 } }),
    enabled: !!orgId,
  });

  useEffect(() => {
    if (!orgId) return;
    const ch = supabase
      .channel(`activity:${orgId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "activity_feed", filter: `organization_id=eq.${orgId}` },
        () => setTick((t) => t + 1),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [orgId]);

  const items = data?.items ?? [];
  const role = data?.role ?? "officer";
  const isSolo = !["org_owner", "admin", "supervisor", "auditor", "site_supervisor", "dispatcher"].includes(role);

  return (
    <div className="min-h-dvh bg-[#0D0D0D] text-white">
      <header className="border-b border-white/10 px-4 pt-5 pb-3 md:px-8">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#C0C0C0]">Mission Ready</div>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-semibold md:text-3xl">
          <ActivityIcon className="h-6 w-6 text-[#CC0000]" />
          {isSolo ? "My Activity" : "Activity"}
        </h1>
        <p className="mt-1 text-sm text-[#999999]">
          {isSolo
            ? "Your own time punches, scans, and reports."
            : "Live stream of all guard activity across your authorized sites."}
        </p>
      </header>

      <div className="px-4 py-5 md:px-8">
        {isLoading ? (
          <div className="flex items-center gap-2 py-12 text-[#C0C0C0]">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center text-sm text-[#999999]">
            No activity yet.
          </div>
        ) : (
          <ul className="divide-y divide-white/5 overflow-hidden rounded-xl border border-white/10 bg-white/[0.03]">
            {items.map((it) => (
              <li key={it.id} className="px-4 py-3 hover:bg-white/[0.04]">
                <div className="flex items-start gap-3">
                  <SeverityDot severity={it.severity} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-white">{it.title}</span>
                      <SourceBadge source={it.source} />
                    </div>
                    {it.body && (
                      <div className="mt-0.5 truncate text-xs text-[#C0C0C0]">{it.body}</div>
                    )}
                    <div className="mt-0.5 text-[11px] text-[#999999]">
                      {it.actorName}
                      {it.siteName && <> · {it.siteName}</>}
                      {" · "}
                      {new Date(it.createdAt).toLocaleString()}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
        {!isLoading && (
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-3 text-xs text-[#C0C0C0] hover:text-white"
          >
            Refresh
          </button>
        )}
      </div>
    </div>
  );
}

function SourceBadge({ source }: { source: string }) {
  const label = SOURCE_LABEL[source] ?? source;
  return (
    <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-wide text-[#C0C0C0]">
      {label}
    </span>
  );
}

function SeverityDot({ severity }: { severity: string }) {
  const color =
    severity === "critical"
      ? "bg-[#CC0000]"
      : severity === "warning"
        ? "bg-amber-400"
        : "bg-emerald-400";
  return <div className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", color)} />;
}
